import React from 'react';
import {
    Button,
    type InputBaseComponentProps,
    Stack,
    Switch,
    Link,
    Box,
    IconButton,
    Popover,
    Typography,
    TextField,
    Autocomplete,
    InputAdornment,
} from '@mui/material';
import {
    mdiAccountCircle,
    mdiChevronDown,
    mdiChevronRight,
    mdiDelete,
    mdiDownload,
    mdiEye,
    mdiPlus,
    mdiShieldAccount,
    mdiStamper,
    mdiPalette,
} from '@mdi/js';

import i18next from 'i18next';
import UserAvatar from './UserAvatar';
import styles from './UserSettingsDialogConfig.module.scss';
import { ReduxUpdater } from '../../../pdf/ReduxUpdater';
import Icon from '@mdi/react';
import { GlobalVariables } from '../../../pdf/GlobalVariables';
import { UserSettingsUpdater } from '../../../pdf/UserSettingsUpdater';
import { ApryseUpdater } from '../../../pdf/ApryseUpdater';
import { GlobalDialogs } from '../../../pdf/GlobalDialogs';
import AlertModalDialog from '../../Layout/Dialogs/AlertModalDialog';
import { ApryseSDK } from '../../../pdf/ApryseSDK';
import { backendApiUrl } from '../../../App';
import type { IUserStamp } from '../../../types';

const urlResetPassword = '//passwordreset.eplass.de';

/** Object that provides data for user settings Dialog. */
export const settingsDataObject = ({
    username,
    first_name,
    last_name,
    email,
    last_password_reset,
    dataProtection,
    pictureLoading,
    picture,
    handleChangeImage,
    handleChangeSwitch,
    isDark,
    handleToggleDarkMode,
    showRuler,
    handleToggleRuler,
}: ISettingsDataAttributes): ISettingsDataObject => {
    const onStampDownloadClick = (stamp: IUserStamp) => {
        const link = document.createElement('a');
        link.download = `${stamp.name}.jpg`;
        link.href = backendApiUrl.stamps.image.replace('{id}', stamp.id);
        link.click();
    };

    const onStampPreviewClick = (ev: React.MouseEvent<HTMLElement>, stamp: IUserStamp) => {
        const stampImageUrl = backendApiUrl.stamps.image.replace('{id}', stamp.id);
        UserSettingsUpdater.showStampPreviewDialog(ev.currentTarget, stampImageUrl);
    };

    const userCategories: ISettingsDialogTabData[] = !ReduxUpdater.lists.userStamps
        ? []
        : ReduxUpdater.lists.userStamps
              .map(stamp => stamp.category)
              .filter((value, index, array) => array.indexOf(value) === index)
              .map(
                  categoryName =>
                      ({
                          label: 'stack',
                          value: categoryName,
                          startAdornment: (
                              <InputAdornment component="div" position="start">
                                  <IconButton
                                      onClick={() =>
                                          UserSettingsUpdater.toggleUserCategoryCollapse(
                                              categoryName,
                                          )
                                      }
                                  >
                                      <Icon
                                          path={
                                              UserSettingsUpdater.checkUserCategoryCollapse(
                                                  categoryName,
                                              )
                                                  ? mdiChevronRight
                                                  : mdiChevronDown
                                          }
                                          size={1}
                                      />
                                  </IconButton>
                              </InputAdornment>
                          ),
                          children: () =>
                              !ReduxUpdater.lists.userStamps ||
                              UserSettingsUpdater.checkUserCategoryCollapse(categoryName) ? (
                                  <></>
                              ) : (
                                  <Box>
                                      {ReduxUpdater.lists.userStamps
                                          .filter(stamp => stamp.category === categoryName)
                                          .map((stamp, i) => (
                                              <Stack
                                                  key={`${categoryName}-${i}`}
                                                  direction="row"
                                                  spacing={0.5}
                                                  sx={{ ml: '1rem', alignItems: 'center' }}
                                              >
                                                  <Box
                                                      key={`box-for-${stamp.id}`}
                                                      sx={{ width: '100%' }}
                                                  >
                                                      {stamp.name}
                                                  </Box>
                                                  <IconButton
                                                      key={`download-for-${stamp.id}`}
                                                      sx={{ padding: '0px' }}
                                                      onClick={() => onStampDownloadClick(stamp)}
                                                  >
                                                      <Icon path={mdiDownload} size={1} />
                                                  </IconButton>
                                                  <IconButton
                                                      key={`preview-for-${stamp.id}`}
                                                      onClick={ev => onStampPreviewClick(ev, stamp)}
                                                      sx={{ padding: '0px' }}
                                                  >
                                                      <Icon path={mdiEye} size={1} />
                                                  </IconButton>
                                                  <IconButton
                                                      key={`delete-for-${stamp.id}`}
                                                      onClick={e => {
                                                          GlobalDialogs.showDialog(
                                                              i18next.t(
                                                                  'settingsUserDialog.askRemoveStampTitle',
                                                                  {
                                                                      count: ApryseUpdater
                                                                          .selectedAnnotations
                                                                          .length,
                                                                  },
                                                              ),
                                                              <AlertModalDialog
                                                                  cancelText={i18next.t(
                                                                      'settingsUserDialog.cancelButtonText',
                                                                  )}
                                                                  confirmText={i18next.t(
                                                                      'settingsUserDialog.confirmButtonText',
                                                                  )}
                                                                  dialogTitle={i18next.t(
                                                                      'settingsUserDialog.askRemoveStampTitle',
                                                                      {
                                                                          count: ApryseUpdater
                                                                              .selectedAnnotations
                                                                              .length,
                                                                      },
                                                                  )}
                                                                  dialogBody={i18next.t(
                                                                      'settingsUserDialog.askRemoveStamp',
                                                                      {
                                                                          count: ApryseUpdater
                                                                              .selectedAnnotations
                                                                              .length,
                                                                      },
                                                                  )}
                                                                  isActionDisabled={false}
                                                                  isOpen={true}
                                                                  onConfirm={() => {
                                                                      UserSettingsUpdater.handleDeleteUserStamp(
                                                                          stamp.id,
                                                                      );
                                                                      GlobalDialogs.closeDialog();
                                                                  }}
                                                                  onCancel={
                                                                      GlobalDialogs.closeDialog
                                                                  }
                                                                  onClose={
                                                                      GlobalDialogs.closeDialog
                                                                  }
                                                              />,
                                                          );
                                                      }}
                                                      sx={{ padding: '0px' }}
                                                  >
                                                      <Icon path={mdiDelete} size={1} />
                                                  </IconButton>
                                                  <Popover
                                                      // id={id}
                                                      open={
                                                          UserSettingsUpdater.addStampPreviewAnchor !==
                                                          null
                                                      }
                                                      anchorEl={
                                                          UserSettingsUpdater.addStampPreviewAnchor
                                                      }
                                                      onClose={() =>
                                                          UserSettingsUpdater.showStampPreviewDialog(
                                                              null,
                                                              '',
                                                          )
                                                      }
                                                      anchorOrigin={{
                                                          vertical: 'bottom',
                                                          horizontal: 'left',
                                                      }}
                                                  >
                                                      <Box
                                                          component="img"
                                                          src={
                                                              UserSettingsUpdater.stampPreviewPicture
                                                          }
                                                          sx={{
                                                              maxHeight: '256px',
                                                              maxWidth: '256px',
                                                              minHeight: '32px',
                                                              minWidth: '32px',
                                                          }}
                                                      />
                                                  </Popover>
                                              </Stack>
                                          ))}
                                  </Box>
                              ),
                      } as ISettingsDialogTabData),
              );

    return {
        profile: {
            name: i18next.t('settingsUserDialog.profile'),
            icon: mdiAccountCircle,
            data: [
                {
                    label: i18next.t('settingsUserDialog.userName'),
                    value: username,
                    children: () => (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ ml: '1rem', alignItems: 'center' }}
                        >
                            <UserAvatar size={50} loading={pictureLoading} picture={picture} />
                            <Button component="label" variant="outlined" className={styles.button}>
                                {i18next.t('settingsUserDialog.chooseImage')}
                                <input
                                    hidden
                                    accept="image/jpg, image/jpeg, image/png"
                                    type="file"
                                    onChange={handleChangeImage}
                                />
                            </Button>
                        </Stack>
                    ),
                },
                {
                    label: i18next.t('settingsUserDialog.firstName'),
                    value: first_name,
                },
                {
                    label: i18next.t('settingsUserDialog.lastName'),
                    value: last_name,
                },
                {
                    label: i18next.t('settingsUserDialog.account'),
                    value: email,
                },
            ],
        },
        security: {
            name: i18next.t('settingsUserDialog.security'),
            icon: mdiShieldAccount,
            data: [
                {
                    label: i18next.t('settingsUserDialog.userName'),
                    value: username,
                },
                {
                    label: i18next.t('settingsUserDialog.lastPasswordReset'),
                    value: last_password_reset.replaceAll('/', '.'),
                    children: () => (
                        <Button
                            className={styles.button2}
                            variant="outlined"
                            sx={{
                                ml: '1rem',
                            }}
                        >
                            <Link
                                className={styles.buttonLink}
                                href={urlResetPassword}
                                underline="none"
                            >
                                {i18next.t('settingsUserDialog.change')}
                            </Link>
                        </Button>
                    ),
                },
                {
                    label: i18next.t('settingsUserDialog.privacy'),
                    value: i18next.t('settingsUserDialog.privacyText'),
                    viewItemComponentProps: {
                        align: 'start',
                        multiline: true,
                        inputProps: {
                            sx: {
                                color: '#C2C2C2', //ToDO: vars.midGray,
                                fontSize: '12px',
                            },
                        },
                    },
                    children: () => (
                        <Switch
                            className={styles.switch}
                            edge="end"
                            onChange={handleChangeSwitch}
                            checked={dataProtection === '1'}
                            color="success"
                        />
                    ),
                },
            ],
        },
        design: {
            name: i18next.t('settingsUserDialog.design'),
            icon: mdiPalette,
            data: [
                {
                    label: i18next.t('settingsUserDialog.darkMode'),
                    value: i18next.t('settingsUserDialog.darkModeText'),
                    viewItemComponentProps: {
                        align: 'start',
                        multiline: true,
                        inputProps: {
                            sx: {
                                color: '#C2C2C2',
                                fontSize: '12px',
                            },
                        },
                    },
                    children: () => (
                        <Switch
                            className={styles.switch}
                            edge="end"
                            onChange={handleToggleDarkMode}
                            checked={isDark}
                            color="success"
                        />
                    ),
                },
                {
                    label: i18next.t('settingsUserDialog.ruler'),
                    value: i18next.t('settingsUserDialog.rulerText'),
                    viewItemComponentProps: {
                        align: 'start',
                        multiline: true,
                        inputProps: {
                            sx: {
                                color: '#C2C2C2',
                                fontSize: '12px',
                            },
                        },
                    },
                    children: () => (
                        <Switch
                            className={styles.switch}
                            edge="end"
                            onChange={handleToggleRuler}
                            checked={showRuler}
                            color="success"
                        />
                    ),
                },
            ],
        },
        stamps: {
            name: i18next.t('settingsUserDialog.stamps'),
            icon: mdiStamper,
            data: [
                {
                    label: 'layout',
                    value: i18next.t('settingsUserDialog.stampCategories'),
                    children: () => (
                        <>
                            <IconButton
                                className={styles.button2}
                                onClick={e => UserSettingsUpdater.showStampDialog(e.currentTarget)}
                            >
                                <Icon path={mdiPlus} size={1} />
                            </IconButton>
                            <Popover
                                // id={id}
                                open={UserSettingsUpdater.addStampDialogAnchor !== null}
                                anchorEl={UserSettingsUpdater.addStampDialogAnchor}
                                onClose={() => UserSettingsUpdater.showStampDialog(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    sx={{ margin: '10px', width: '250px' }}
                                >
                                    <Typography sx={{ p: 2 }}>
                                        {i18next.t('settingsUserDialog.addStamp')}
                                    </Typography>
                                    <TextField
                                        id="name-basic"
                                        label={i18next.t('stampUpload.name')}
                                        variant="outlined"
                                        onChange={e => {
                                            GlobalVariables.userStamp.name = e.target.value;
                                        }}
                                    />
                                    <Autocomplete
                                        id="category-basic"
                                        freeSolo
                                        style={{ width: '100%' }}
                                        value={GlobalVariables.userStamp.category}
                                        options={ApryseSDK.userCategoriesList.map(
                                            item => item.title,
                                        )}
                                        onInputChange={(_, value) => {
                                            GlobalVariables.userStamp.category = value;
                                        }}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label={i18next.t('stampUpload.category')}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    type: 'search',
                                                }}
                                                onChange={e => {
                                                    GlobalVariables.userStamp.category =
                                                        e.target.value;
                                                }}
                                            />
                                        )}
                                        sx={{
                                            marginTop: '5px',
                                        }}
                                    />
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        className={styles.button}
                                    >
                                        {i18next.t('settingsUserDialog.chooseImage')}
                                        <input
                                            hidden
                                            accept="image/jpg, image/jpeg, image/png"
                                            type="file"
                                            onChange={e => {
                                                if (
                                                    !e.target.files ||
                                                    e.target.files.length === 0
                                                ) {
                                                    return;
                                                }
                                                const stampFile = e.target.files[0];
                                                if (
                                                    ![
                                                        'image/jpg',
                                                        'image/jpeg',
                                                        'image/png',
                                                    ].includes(stampFile.type)
                                                ) {
                                                    // ToDO: error...
                                                    return;
                                                }
                                                GlobalVariables.userStampFile = stampFile;
                                            }}
                                        />
                                    </Button>
                                    <Stack direction="row" spacing={0.5}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() =>
                                                UserSettingsUpdater.showStampDialog(null)
                                            }
                                            sx={{
                                                width: '50%',
                                            }}
                                        >
                                            {i18next.t('settingsUserDialog.cancelUpload')}
                                        </Button>
                                        <Button
                                            className={styles.button3}
                                            variant="contained"
                                            color="success"
                                            onClick={UserSettingsUpdater.handleUploadUserStamp}
                                            sx={{
                                                width: '50%',
                                            }}
                                        >
                                            {i18next.t('settingsUserDialog.uploadStamp')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Popover>
                        </>
                    ),
                },
                ...userCategories,
            ],
        },
    };
};

/**
 * Interface that describes Settings Dialog Tab's Data.
 */
export interface ISettingsDialogTabData {
    startAdornment?: JSX.Element;
    label: string;
    value: string;
    viewItemComponentProps?: {
        align?: string;
        multiline?: boolean;
        direction?: string;
        inputProps?: InputBaseComponentProps;
    };

    children?: () => JSX.Element;
}

/**
 * Interface that describes Setting's Dialog Tab.
 */
interface ISettingsDialogTab {
    name: string;
    icon: string;
    data: ISettingsDialogTabData[];
}

/**
 * Interface that describes Setting's Dialog Tab.
 */
interface IStampsDialogTab {
    name: string;
    icon: string;
    data: ISettingsDialogTabData[];
}

/**
 * Interface that describes Setting's Dialog Data Object.
 */
export interface ISettingsDataObject {
    profile: ISettingsDialogTab;
    security: ISettingsDialogTab;
    design: ISettingsDialogTab;
    stamps: IStampsDialogTab;
}

/**
 * Interface that describes Setting's Dialog Data Attributes.
 */
export interface ISettingsDataAttributes {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    last_password_reset: string;
    dataProtection: string;
    pictureLoading: boolean;
    handleChangeImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleChangeSwitch: (e: React.ChangeEvent<HTMLInputElement>) => void;
    picture?: string;
    isDark: boolean;
    handleToggleDarkMode: () => void;
    showRuler: boolean;
    handleToggleRuler: () => void;
}
